import { useState, useEffect, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errText } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Sparkle, CircleNotch, PaperPlaneTilt, LockKey } from "@phosphor-icons/react";

const SUGGESTIONS = [
  "Did I upload my closing statement yet?",
  "What's the individual filing deadline?",
  "What's your office address?",
];

export function AssistantChat() {
  const [q, setQ] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const scrollRef = useRef();

  const { data: history } = useQuery({
    queryKey: ["assistant-history"],
    queryFn: () => api.get("/portal/assistant/history").then((r) => r.data),
  });

  useEffect(() => {
    if (history?.id) { setSessionId(history.id); setMsgs(history.messages || []); }
  }, [history]);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }); }, [msgs]);

  const ask = useMutation({
    mutationFn: (question) => api.post("/portal/assistant", { question, session_id: sessionId }),
    onSuccess: ({ data }) => {
      setSessionId(data.session_id);
      setMsgs((m) => [...m, { role: "assistant", content: data.answer }]);
    },
    onError: (e) => toast.error(errText(e)),
  });

  const send = (text) => {
    const question = (text || q).trim();
    if (!question || ask.isPending) return;
    setMsgs((m) => [...m, { role: "client", content: question }]);
    setQ("");
    ask.mutate(question);
  };

  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-5" data-testid="assistant-chat">
      <h3 className="text-sm font-medium text-zinc-900 mb-1 flex items-center gap-1.5"><Sparkle size={15} /> Ask about your documents & deadlines</h3>
      <p className="text-[11px] text-zinc-500 mb-3 flex items-center gap-1"><LockKey size={11} /> Answers use only YOUR data — available 24/7, no billable hour needed.</p>
      {msgs.length === 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {SUGGESTIONS.map((s) => (
            <button key={s} data-testid="assistant-suggestion-chip" onClick={() => send(s)}
              className="text-[10px] text-zinc-600 border border-zinc-200 rounded-full px-2.5 py-1 hover:bg-zinc-50 transition-colors duration-150">{s}</button>
          ))}
        </div>
      )}
      {msgs.length > 0 && (
        <div ref={scrollRef} className="space-y-2 max-h-56 overflow-y-auto mb-3 pr-1">
          {msgs.map((m, i) => (
            <div key={i} className={`max-w-[88%] ${m.role === "client" ? "ml-auto" : ""}`}>
              <p className={`text-xs rounded-md px-3 py-2 ${m.role === "client" ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-800"}`} data-testid={`assistant-msg-${m.role}`}>{m.content}</p>
            </div>
          ))}
          {ask.isPending && <p className="text-[11px] text-zinc-400 flex items-center gap-1"><CircleNotch size={12} className="animate-spin" /> Checking your records…</p>}
        </div>
      )}
      <div className="flex gap-2">
        <Input data-testid="assistant-input" placeholder='e.g. "How much was my last invoice?"' value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} className="h-8 text-xs" />
        <Button data-testid="assistant-send-btn" size="sm" className="h-8" disabled={!q.trim() || ask.isPending} onClick={() => send()}>
          {ask.isPending ? <CircleNotch size={13} className="animate-spin" /> : <PaperPlaneTilt size={13} />}
        </Button>
      </div>
    </div>
  );
}
